/**
 * ProcessGraphChart_4 — CONTENT alternative to ProcessGraphChart (blog06).
 *
 * Same 5 nodes and 4 directed edges as the original, re-read as TIME instead of
 * topology: a vertical execution sequence where each node is a lane row in run
 * order, and the human review gate is a full-width pause band the flow must cross.
 *
 * Finding this surfaces: execution *stops* at the gate — the two bot steps run,
 * then nothing reaches the outside world (or the log) until a human releases the
 * single edge that crosses the band.
 *
 * Zero invented numbers: node names, categories and edge colors are copied verbatim
 * from ProcessGraphChart.tsx; the only added values are the execution-order indices
 * (`index + 1` over the NODES array, visible below) and category counts derived in
 * code for the caption.
 *
 * Rendered as DOM (a swimlane/sequence view), not ECharts — height maps to minHeight.
 */

const BRAND = {
  teal: '#0AB3BF',
  info: '#3B82F6',
  warning: '#F59E0B',
  error: '#EF4444',
  slate: '#334155',
  muted: '#64748B',
};

// Verbatim from ProcessGraphChart.tsx — Step, Gate, External, Log.
const CATEGORY_META = [
  { label: 'Step (bot)', color: BRAND.teal },
  { label: 'Approval gate', color: BRAND.warning },
  { label: 'External action', color: BRAND.error },
  { label: 'Log row', color: BRAND.info },
];

// Verbatim from ProcessGraphChart.tsx (execution order = array order).
const NODES = [
  { name: 'Qualify lead', category: 0 },
  { name: 'Draft outreach', category: 0 },
  { name: 'Human review gate', category: 1 },
  { name: 'Send email', category: 2 },
  { name: 'Log the run', category: 3 },
];

// Verbatim from ProcessGraphChart.tsx (edge colors preserved: red = crosses to the
// outside world, blue = logged after the run).
const EDGES = [
  { source: 'Qualify lead', target: 'Draft outreach' },
  { source: 'Draft outreach', target: 'Human review gate' },
  { source: 'Human review gate', target: 'Send email', color: BRAND.error },
  { source: 'Send email', target: 'Log the run', color: BRAND.info },
];

const GATE_CATEGORY = 1;

/** ONE shared formatter — visible lane labels and title-tooltips both use it. */
const formatNodeLabel = (name: string, order: number) => `${order}. ${name}`;

export const ProcessGraphChart_4 = ({ height = 440 }: { height?: number }) => {
  const botSteps = NODES.filter((n) => n.category === 0).length;
  const postGate = NODES.filter((n) => n.category !== 0 && n.category !== GATE_CATEGORY).length;

  return (
    <div
      className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-4"
      style={{ minHeight: height }}
    >
      {/* Category legend */}
      <div className="mb-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {CATEGORY_META.map((c) => (
          <span key={c.label} className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }} />
            {c.label}
          </span>
        ))}
      </div>

      {/* Vertical execution sequence */}
      <div className="mx-auto max-w-xl">
        {NODES.map((node, index) => {
          const order = index + 1; // execution-order index, derived from NODES array order
          const label = formatNodeLabel(node.name, order);
          const incoming = EDGES.find((e) => e.target === node.name);
          const connectorColor = incoming?.color ?? BRAND.slate;
          const isGate = node.category === GATE_CATEGORY;
          const meta = CATEGORY_META[node.category];

          return (
            <div key={node.name}>
              {/* Incoming edge connector (one per edge, in execution order) */}
              {incoming && (
                <div className="flex justify-center" title={`${incoming.source} → ${incoming.target}`}>
                  <div
                    className="relative h-8 w-0.5"
                    style={{ backgroundColor: connectorColor }}
                    aria-label={`${incoming.source} to ${incoming.target}`}
                  >
                    <span
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2"
                      style={{
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        borderTop: `7px solid ${connectorColor}`,
                      }}
                    />
                  </div>
                </div>
              )}

              {isGate ? (
                /* Full-width pause band: the flow must cross this to continue */
                <div
                  title={label}
                  className="rounded-lg border-y-4 px-4 py-3 text-center"
                  style={{ borderColor: BRAND.warning, backgroundColor: '#FFFBEB' }}
                >
                  <p className="text-label-xs font-bold uppercase tracking-widest" style={{ color: '#92400E' }}>
                    Execution pauses — human review required
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-700">{label}</p>
                </div>
              ) : (
                /* Lane row: one node in execution order */
                <div
                  title={label}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5"
                >
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: meta.color }}
                  >
                    {order}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-700">{node.name}</span>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-label-xs font-semibold text-white"
                    style={{ backgroundColor: meta.color }}
                  >
                    {meta.label}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. The same graph read as time: {botSteps} bot steps run in sequence, then the flow hits the pause band —
        the {postGate} remaining nodes (the external action and the log) sit on the far side and cannot happen until a
        human releases the one edge that crosses to the outside world.
      </p>
    </div>
  );
};
